import { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addIsEnableIfMissing } from "../util/addIsEnableIfMissing";
import { groupPermissionsByCategory } from "../util/groupPermissionsByCategory";
import { GroupedPermissions, TNewRole } from "../type";
import useBreadcrumbs from "@/hooks/store/useBreadcrumbs";
import useCreateRole from "@/hooks/query/acl/useCreateRole";
import AddRolePermissionStep from "./AddRolePermissionStep";
import AddRoleDetailStep from "./AddRoleDetailStep";
import MultiStepFormLayout from "@/components/layout/multiStepFormLayout/MultiStepFormLayout";
import { useSidebarStore } from "@/hooks/general/useSidebarStore";
import { useAddRoleStore } from "./useAddRoleStore";
import { Toast } from "@/components/kit";
import { useGetTrioPermission } from "@/hooks/query/acl/useGetTrioPermission";

function areAllPermissionsDisabled(obj: GroupedPermissions): boolean {
  const checkAllFalse = (input: any): boolean => {
    if (Array.isArray(input)) {
      return input.every((item) => checkAllFalse(item));
    } else if (typeof input === "object" && input !== null) {
      for (const key in input) {
        if (key === "isEnable") {
          if (input[key] !== false) return false;
        } else {
          if (!checkAllFalse(input[key])) return false;
        }
      }
    }
    return true;
  };

  return checkAllFalse(obj);
}

function AddRoleScratch() {
  const navigate = useNavigate();
  const { data, isSuccess } = useGetTrioPermission();
  const [groupedPermissions, setGroupedPermissions] =
    useState<GroupedPermissions>();

  const { setRoutes } = useBreadcrumbs();
  const { mutate, isPending } = useCreateRole();

  const { setSecondSidebar } = useSidebarStore();
  const { name, description, reset } = useAddRoleStore();

  useEffect(() => {
    if (data?.permissions) {
      handleStorePermissions();
    }
  }, [isSuccess]);

  useEffect(() => {
    reset();
    setSecondSidebar(false);
    return () => {
      setSecondSidebar(true);
    };
  }, []);
  useLayoutEffect(() => {
    setRoutes([
      {
        name: "Admins center",
        path: "/admins",
      },
      {
        name: "Organizational role",
        path: "/acl",
      },
      {
        name: "Add new role",
        description:
          "Creating a new role in Trio lets you customize access for different team responsibilities. Instead of assigning broad permissions, you can define exactly what each role can view or manage, like profile actions, employee data, or policy settings. This helps scale admin access as your team grows while maintaining control and security.",
      },
    ]);

    return () => {
      setRoutes([]);
    };
  }, []);

  const { data: permissionsData, isSuccess: isPermissionsSuccess } =
    useGetTrioPermission();

  const handleStorePermissions = () => {
    const role = addIsEnableIfMissing(permissionsData?.permissions ?? []);
    const initialGroupedPermissions = groupPermissionsByCategory(role);
    setGroupedPermissions(initialGroupedPermissions);
  };

  useEffect(() => {
    if (data?.permissions) {
      handleStorePermissions();
    }
  }, [isPermissionsSuccess]);

  const handleSaveRole = () => {
    if (!groupedPermissions) return;
    const permissions = Object.values(groupedPermissions).flatMap(
      (subPermissions) =>
        Object.values(subPermissions).flatMap((permissionArray) =>
          permissionArray
            .filter((permission) => permission.isEnable)
            .map((permission) => permission.name),
        ),
    );

    const newRole: TNewRole = {
      description,
      name,
      permissionNames: permissions,
      // assigneeIds: selectedUserIdList,
    };
    mutate(newRole, {
      onSuccess: () => {
        reset();
        navigate(-1);
        Toast({
          status: "success",
          description: "A new role has been added.",
          title: "success",
        });
      },
    });
  };

  return (
    <>
      <MultiStepFormLayout
        title="Add role"
        description="The good subtext for each steps"
        saveButtonText="saveButton text"
        finishButtonText="Save new access"
        showSaveButton={false}
        showCancelButton
        onCancel={() => navigate("/acl")}
        isNextDisabled={(step) =>
          (step === 1 && name.trim() !== "") ||
          (step === 2 &&
            !!groupedPermissions &&
            !areAllPermissionsDisabled(groupedPermissions))
        }
        isValid={
          name.trim() !== "" &&
          groupedPermissions &&
          !areAllPermissionsDisabled(groupedPermissions)
        }
        steps={[
          {
            stepNumber: 1,
            title: "Role details",
            component: <AddRoleDetailStep />,
          },
          {
            stepNumber: 2,
            title: "Role permissions",
            component: groupedPermissions ? (
              <AddRolePermissionStep
                groupedPermissions={groupedPermissions}
                setGroupedPermissions={setGroupedPermissions}
              />
            ) : null,
          },
        ]}
        onSubmit={handleSaveRole}
        isLoading={isPending}
      />
    </>
  );
}

export default AddRoleScratch;
